import indexingPolicy from "../data/indexingPolicy.json";
import { readerGuideByPath } from "../data/readerGuides20261006";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { seoConfig } from "../data/seoConfig";
import seoTitles from "../data/seoTitles.json";
import { roboticsResearchEnhancements } from "../data/roboticsResearchEnhancements";
import { brandAlternateNames, brandIdentity, buildOrganizationSchema } from "../data/brand";
import { getLocalizedAlternates } from "../data/localizedPriorityPages";

function normalizeSeoPath(path = "/") {
  if (!path || path === "/") return "/";

  const safePath = String(path).startsWith("/") ? String(path) : `/${path}`;
  const pathname = safePath.split(/[?#]/)[0];
  return pathname.replace(/\/+/g, "/").replace(/\/$/, "") || "/";
}

export function buildCanonicalUrl(path = "/") {
  return `${seoConfig.baseUrl}${normalizeSeoPath(path)}`;
}

const organizationSchema = buildOrganizationSchema();

function schemaTypes(node) {
  const value = node?.["@type"];
  return Array.isArray(value) ? value : value ? [value] : [];
}

function breadcrumbNameFromItem(item, index) {
  const explicitName = item?.name || item?.item?.name || item?.label || item?.title;
  if (explicitName) return String(explicitName);

  const rawUrl =
    typeof item?.item === "string"
      ? item.item
      : item?.item?.["@id"] || item?.url;

  if (rawUrl) {
    try {
      const pathname = new URL(rawUrl, seoConfig.baseUrl).pathname;
      if (pathname === "/") return "Home";
      const segment = decodeURIComponent(pathname.split("/").filter(Boolean).pop() || "");
      if (segment) {
        return segment
          .replace(/[-_]+/g, " ")
          .replace(/\b\w/g, (character) => character.toUpperCase());
      }
    } catch {
      // Fall through to a deterministic visible label.
    }
  }

  return index === 0 ? "Home" : `Page ${index + 1}`;
}

function sanitizeStructuredData(node) {
  if (Array.isArray(node)) return node.map(sanitizeStructuredData).filter(Boolean);
  if (!node || typeof node !== "object") return node;

  const sanitized = Object.fromEntries(
    Object.entries(node).map(([key, value]) => [key, sanitizeStructuredData(value)]),
  );
  const types = schemaTypes(sanitized);

  if (
    types.includes("Product") &&
    !sanitized.offers &&
    !sanitized.review &&
    !sanitized.aggregateRating
  ) {
    const safeTypes = types.filter((schemaType) => schemaType !== "Product");
    sanitized["@type"] = safeTypes.length
      ? Array.isArray(sanitized["@type"])
        ? safeTypes
        : safeTypes[0]
      : "Thing";
  }

  // Dataset rights must come from the source data; do not invent a license.

  if (types.includes("BreadcrumbList")) {
    const breadcrumbItems = Array.isArray(sanitized.itemListElement)
      ? sanitized.itemListElement
      : [];
    sanitized.itemListElement = breadcrumbItems
      .filter((item) => item && typeof item === "object")
      .map((item, index) => ({
        ...item,
        "@type": "ListItem",
        position: Number(item.position) || index + 1,
        name: breadcrumbNameFromItem(item, index),
      }));
  }

  return sanitized;
}

const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${seoConfig.baseUrl}/#website`,
  url: `${seoConfig.baseUrl}/`,
  name: brandIdentity.name,
  alternateName: brandAlternateNames,
  publisher: {
    "@id": `${seoConfig.baseUrl}/#organization`,
  },
};

export default function SEO({
  title = seoConfig.defaultTitle,
  description = seoConfig.defaultDescription,
  path = "/",
  type = "website",
  image,
  imageAlt = "",
  schema,
  noindex = false,
  publishedTime,
  modifiedTime,
  author,
  section,
  tags = [],
  lang = "en",
  alternates = null,
}) {
  const location = useLocation();
  const currentPath = normalizeSeoPath(location.pathname || path);
  const fallbackPath = normalizeSeoPath(path);
  const canonical = buildCanonicalUrl(currentPath);
  const localeNoindex = new RegExp(indexingPolicy.unreviewedLocalePattern).test(currentPath);
  const isNoindex = noindex || localeNoindex;
  const localizedAlternates = (isNoindex ? [] : Array.isArray(alternates)
    ? alternates
    : getLocalizedAlternates(currentPath)).filter((item) => !new RegExp(indexingPolicy.unreviewedLocalePattern).test(item.href));
  const englishAlternate = localizedAlternates.find((item) => item.locale === "en");
  const hasXDefaultAlternate = localizedAlternates.some((item) => item.hreflang === "x-default");
  const exposeCanonical = !noindex;
  const resolvedTitle =
    seoTitles[currentPath] || seoTitles[fallbackPath] || title;
  const robots = isNoindex
    ? "noindex, follow"
    : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";
  const preferredImage = image || (type === "article" ? null : seoConfig.defaultImage);
  const resolvedImage = preferredImage
    ? preferredImage.startsWith("http") ? preferredImage : `${seoConfig.baseUrl}${preferredImage.startsWith("/") ? "" : "/"}${preferredImage}`
    : null;
  const rawSchemaItems = Array.isArray(schema)
    ? schema
    : schema
      ? [schema]
      : [];
  const hasResearchUpdate = !readerGuideByPath[currentPath] && Boolean(roboticsResearchEnhancements[currentPath]);
  const schemaItems = rawSchemaItems.map((rawItem) => {
    const item = sanitizeStructuredData(rawItem);
    if (["Article", "NewsArticle", "TechArticle"].some((t) => [item?.["@type"]].flat().includes(t)) && resolvedImage) item.image = [resolvedImage];
    const schemaTypes = Array.isArray(item?.["@type"])
      ? item["@type"]
      : [item?.["@type"]];
    if (
      !hasResearchUpdate ||
      !schemaTypes.some((schemaType) =>
        ["Article", "NewsArticle", "TechArticle"].includes(schemaType),
      )
    )
      return item;
    return {
      ...item,
      dateModified:
        roboticsResearchEnhancements[currentPath]?.reviewed || modifiedTime || item.dateModified,
    };
  });
  const homepageSchema =
    currentPath === "/" ? [organizationSchema, websiteSchema] : [];

  return (
    <Helmet>
      <html lang={lang} />
      <title>{resolvedTitle}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      {exposeCanonical && <link rel="canonical" href={canonical} />}
      {exposeCanonical && localizedAlternates.map((item) => (
        <link
          key={`alternate-${item.hreflang}`}
          rel="alternate"
          hrefLang={item.hreflang}
          href={buildCanonicalUrl(item.href)}
        />
      ))}
      {exposeCanonical && englishAlternate && !hasXDefaultAlternate && (
        <link rel="alternate" hrefLang="x-default" href={buildCanonicalUrl(englishAlternate.href)} />
      )}

      <meta property="og:site_name" content={seoConfig.siteName} />
      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      {exposeCanonical && <meta property="og:url" content={canonical} />}
      {resolvedImage && <meta property="og:image" content={resolvedImage} />}
      {resolvedImage && imageAlt && <meta property="og:image:alt" content={imageAlt} />}
      {type === "article" && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === "article" && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === "article" && author && (
        <meta property="article:author" content={author} />
      )}
      {type === "article" && section && (
        <meta property="article:section" content={section} />
      )}
      {type === "article" &&
        tags.map((tag) => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={seoConfig.twitterHandle} />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={description} />
      {resolvedImage && <meta name="twitter:image" content={resolvedImage} />}
      {resolvedImage && imageAlt && <meta name="twitter:image:alt" content={imageAlt} />}
      {exposeCanonical && <meta name="twitter:url" content={canonical} />}

      {[...homepageSchema, ...schemaItems].map((item, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(item).replace(/</g, "\\u003c")}
        </script>
      ))}
    </Helmet>
  );
}
